import { Badge } from 'primereact/badge';
import { TreeNode } from 'primereact/treenode';
import {
  TreeSelect,
  TreeSelectChangeEvent,
  TreeSelectCheckboxSelectionKeyType,
  TreeSelectSelectionKeysType,
} from 'primereact/treeselect';
import { classNames } from 'primereact/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CompanyFilterDrodpdownItem } from '../../../hooks/api/company';
import styles from './CompanyFilter.module.css';

type CompanyFilterProps = {
  companies: CompanyFilterDrodpdownItem[];
  isLoading: boolean;
  onCompanyFilterChange: (companyIds: string[], companyBpns: string[]) => void;
};

interface SelectedNode {
  [key: string]: TreeSelectCheckboxSelectionKeyType;
}

const CompanyFilter = (props: CompanyFilterProps): JSX.Element => {
  const { companies, isLoading, onCompanyFilterChange } = props;
  const { t } = useTranslation();

  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState<SelectedNode>({});

  // Convert your product tasks to TreeNode array
  const [treeNodes, setTreeNodes] = useState<TreeNode[]>([]);

  // Initialize company list only once when productTasks loads
  useEffect(() => {
    if (companies) {
      const nodes = companies.map((company) => {
        return {
          key: String(company?.id ?? company?.companyBpn ?? ''),
          label: company?.companyName ?? company?.companyBpn ?? '',
        };
      });
      setTreeNodes(nodes);
    }
  }, [companies]);

  const handleChange = (e: TreeSelectChangeEvent) => {
    const selectedKeysMap = (e.value as SelectedNode) || {};
    setSelectedCompanies(selectedKeysMap);

    // Map back to your original object structure
    const senderCompanyIds: string[] = [];
    const senderCompanyBPNs: string[] = [];

    Object.keys(selectedKeysMap).forEach((key) => {
      const selectedCompany = companies?.find((item) => {
        return String(item.companyBpn) === key || String(item.id) === key;
      });
      if (selectedCompany?.id) {
        senderCompanyIds.push(`${selectedCompany.id}`);
      } else if (selectedCompany?.companyBpn) {
        senderCompanyBPNs.push(`${selectedCompany.companyBpn}`);
      }
    });

    onCompanyFilterChange(senderCompanyIds, senderCompanyBPNs);
  };

  const valueTemplate = (nodes: TreeNode[] | TreeNode | null | undefined) => {
    // If nothing is selected, show the placeholder
    if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
      return (
        <span className='line-height-2'>
          {t('exportProductTasksDialog.companyFilter.placeholder')}
        </span>
      );
    }

    // Display the number of selected nodes
    return (
      <div className='flex flex-row gap-1 line-height-2'>
        <span className='text-primary'>
          {t('exportProductTasksDialog.companyFilter.placeholder')}:
        </span>
        <Badge className='flex align-items-center justify-content-center' value={nodes.length} />
      </div>
    );
  };

  const nodeTemplate = (node: TreeNode) => {
    return (
      <div className='flex-1 w-full p-2'>
        <div className='flex flex-row justify-content-between align-items-center'>
          <span className='font-normal break-all'>{node.label}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={classNames('flex align-items-center', styles['company-filter'])}>
      <TreeSelect
        value={selectedCompanies as TreeSelectSelectionKeysType}
        options={treeNodes}
        selectionMode='multiple'
        valueTemplate={valueTemplate}
        nodeTemplate={nodeTemplate}
        onChange={handleChange}
        onHide={() => {
          setIsOverlayVisible(false);
        }}
        onShow={() => {
          setIsOverlayVisible(true);
        }}
        showClear
        filter
        filterInputAutoFocus
        disabled={isLoading}
        filterPlaceholder={t('exportProductTasksDialog.companyFilter.filterPlaceholder')}
        className={classNames(
          styles['company-filter'],
          isOverlayVisible ? styles['company-filter-open'] : styles['company-filter-close'],
          'border-none',
        )}
        pt={{
          root: {
            className: styles['root'],
          },
          labelContainer: {
            className: 'max-h-full w-full',
          },
          label: {
            className: styles['label'],
          },
          closeButton: {
            className: 'hidden',
          },
          wrapper: {
            className: styles['company-filter'],
          },
          filterContainer: {
            className: styles['filter-container'],
          },
          clearIcon: {
            className: classNames(
              'text-black-alpha-60',
              {
                hidden:
                  selectedCompanies === undefined || Object.keys(selectedCompanies).length === 0,
              },
              styles['clear-icon'],
            ),
          },
          trigger: {
            className: classNames({
              'hidden w-0 p-0 overflow-hidden shrink-0':
                selectedCompanies && Object.keys(selectedCompanies).length > 0,
            }),
          },
        }}
      />
    </div>
  );
};

export default CompanyFilter;
